import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import { getBorrowers } from '../../../services/request/Borrowers';
import { BorrowerAPIItem } from '../../../models/LendingModels';
import WelcomeBrochure from './WelcomeBrochure';
import { toast } from 'react-toastify';

interface BrochureViewerProps {
  open: boolean;
  onClose: () => void;
  initialBorrowerId?: string;
}

const BrochureViewer: React.FC<BrochureViewerProps> = ({
  open,
  onClose,
  initialBorrowerId,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBorrower, setSelectedBorrower] = useState<BorrowerAPIItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load borrower if initialBorrowerId is provided
  useEffect(() => {
    if (initialBorrowerId && open) {
      loadBorrowerById(initialBorrowerId);
    }
  }, [initialBorrowerId, open]);

  const loadBorrowerById = async (borrowerId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getBorrowers(0, 1000, {});
      
      if (response.code === 200 && response.result) {
        const borrower = response.result.find(
          (b: BorrowerAPIItem) => b.id === borrowerId
        );

        if (borrower) {
          setSelectedBorrower(borrower);
          setSearchTerm(borrower.name);
          toast.success('Customer loaded!');
        } else {
          setError('Customer not found');
        }
      }
    } catch (err) {
      setError('Error loading customer');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a customer name or ID');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Search for borrower by name or ID
      const response = await getBorrowers(0, 100, {});
      
      if (response.code === 200 && response.result) {
        const borrower = response.result.find(
          (b: BorrowerAPIItem) =>
            b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            b.id.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (borrower) {
          setSelectedBorrower(borrower);
          toast.success('Customer found!');
        } else {
          setError('No customer found with that name or ID');
          setSelectedBorrower(null);
        }
      } else {
        setError('Failed to search for customer');
      }
    } catch (err) {
      setError('Error searching for customer');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedBorrower(null);
    setSearchTerm('');
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '95vh',
          m: 2,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <TextField
              placeholder="Search by customer name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              fullWidth
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading || !searchTerm.trim()}
              sx={{ minWidth: 100 }}
            >
              {loading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {selectedBorrower ? (
          <Box sx={{ p: 2 }}>
            <WelcomeBrochure
              customerName={selectedBorrower.name}
              outstandingAmount={selectedBorrower.totalOutstanding}
            />
          </Box>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="info">
              Search for a customer to generate their personalized welcome brochure
            </Alert>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BrochureViewer;
